import { IconPanelLeft } from '@developerz/ui';
import { createEffect, type JSX, on } from 'solid-js';
import { t } from '../i18n';
import { buttonClass } from './ui/Button';

// The rail's foot toggle (#7710): collapse the `lg` rail to its icon rail, and
// expand it back. The pointer door to the `[` key (`lib/hotkeys.ts`), and the
// only door on a layout where `[` needs AltGr. Split out of `AppSidebar`, which
// sits at biome's 120-line function budget.
//
// Its NAME is static, the `AppNavHeading` pattern: "Sidebar" stays findable as
// "Sidebar" and `aria-expanded` carries the state. The tooltip says what a press
// does next, in both states (`data-rail-tip-always`, read by `RailTip`).
// `display: none` below `lg` (`_sidebar.scss`, like `.app-sidebar__close`), so the
// phone drawer never shows it.
//
// A collapse hides some controls outright (`display: none`): the fold headings and
// the org switcher. Focus left on one of them would sit on nothing, so on the
// collapsing edge it moves here. The list is closed and matched by selector, not
// by computed style, so the rule holds where no stylesheet is loaded.

/** The controls the collapsed rail sets to `display: none` (`_sidebar.scss`). */
const HIDDEN_WHEN_COLLAPSED = '.app-nav__label, .org-switcher';

export function RailToggle(props: {
  readonly collapsed: boolean;
  readonly onToggle: () => void;
  /** The id of the rail it collapses (`NAV_DRAWER_ID`). */
  readonly controls: string;
}): JSX.Element {
  let button: HTMLButtonElement | undefined;
  createEffect(
    on(
      () => props.collapsed,
      (collapsed) => {
        if (!collapsed) return;
        const active = document.activeElement;
        if (active instanceof Element && active.closest(HIDDEN_WHEN_COLLAPSED) !== null) {
          button?.focus();
        }
      },
      { defer: true },
    ),
  );
  return (
    <button
      ref={button}
      type="button"
      class={`${buttonClass({ variant: 'ghost', size: 'icon-sm' })} app-sidebar__rail-toggle`}
      aria-label={t('nav.rail_toggle')}
      aria-expanded={!props.collapsed}
      aria-controls={props.controls}
      aria-keyshortcuts="["
      data-rail-tip={props.collapsed ? t('nav.rail_expand') : t('nav.rail_collapse')}
      data-rail-tip-always=""
      onClick={() => props.onToggle()}
    >
      <IconPanelLeft size="1rem" />
    </button>
  );
}
